import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import {
  ShoppingBag,
  Package,
  RefreshCw,
  FileText,
  LogOut,
  User,
  Home,
  ExternalLink,
  Calendar,
  CreditCard,
  X
} from "lucide-react";

const COMPANY_LOGO = "https://customer-assets.emergentagent.com/job_bizcommerce-19/artifacts/7hv4d1j9_LOGO%20PNG.png";

const CustomerDashboard = () => {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [signatures, setSignatures] = useState([]);
  const [activeTab, setActiveTab] = useState("orders");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [ordersRes, subsRes, sigsRes] = await Promise.all([
        fetch(`${API}/orders`, { headers, credentials: "include" }),
        fetch(`${API}/subscriptions`, { headers, credentials: "include" }),
        fetch(`${API}/my-signatures`, { headers, credentials: "include" })
      ]);

      if (ordersRes.ok) setOrders(await ordersRes.json());
      if (subsRes.ok) setSubscriptions(await subsRes.json());
      if (sigsRes.ok) setSignatures(await sigsRes.json());
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async (subscriptionId) => {
    try {
      const response = await fetch(`${API}/subscriptions/${subscriptionId}/cancel`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });
      
      if (response.ok) {
        toast.success("Subscription cancelled");
        fetchData();
      } else {
        toast.error("Failed to cancel subscription");
      }
    } catch (error) {
      toast.error("Failed to cancel subscription");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const tabs = [
    { id: "orders", label: "My Orders", icon: Package },
    { id: "subscriptions", label: "Subscriptions", icon: RefreshCw },
    { id: "agreements", label: "Agreements", icon: FileText }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-3">
                <img src={COMPANY_LOGO} alt="V" className="h-10 w-auto" />
                <div className="h-8 w-px bg-slate-300" />
                <span className="font-brand text-xl font-bold text-slate-900">VEYRONIX</span>
              </Link>
            </div>

            <div className="flex items-center gap-4">
              <Link to="/store">
                <Button variant="outline" className="rounded-full" data-testid="browse-store-btn">
                  <ShoppingBag className="h-4 w-4 mr-2" />
                  Browse Store
                </Button>
              </Link>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                  {user?.picture ? (
                    <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
                  ) : (
                    <User className="h-5 w-5 text-indigo-600" />
                  )}
                </div>
                <div className="hidden sm:block">
                  <p className="font-medium text-slate-900 text-sm">{user?.name}</p>
                  <p className="text-xs text-slate-500">{user?.email}</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleLogout}
                className="text-slate-500 hover:text-slate-700"
                data-testid="logout-btn"
              >
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2" data-testid="customer-dashboard">
            Welcome back, {user?.name?.split(" ")[0]}!
          </h1>
          <p className="text-slate-500">Manage your orders and subscriptions from your dashboard.</p>
        </div>

        {/* Stats */}
        <div className="grid sm:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
                <Package className="h-6 w-6 text-indigo-600" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold text-slate-900">{orders.length}</p>
                <p className="text-sm text-slate-500">Total Orders</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                <RefreshCw className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold text-slate-900">
                  {subscriptions.filter(s => s.status === "active").length}
                </p>
                <p className="text-sm text-slate-500">Active Subscriptions</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                <FileText className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold text-slate-900">{signatures.length}</p>
                <p className="text-sm text-slate-500">Signed Agreements</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="border-b border-slate-200">
            <div className="flex">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors border-b-2 -mb-px ${
                    activeTab === tab.id
                      ? "border-indigo-600 text-indigo-600"
                      : "border-transparent text-slate-500 hover:text-slate-700"
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <tab.icon className="h-4 w-4" />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <div className="p-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-10 h-10 border-4 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" />
              </div>
            ) : activeTab === "orders" ? (
              orders.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-heading font-semibold text-slate-900 mb-2">No orders yet</h3>
                  <p className="text-slate-500 mb-4">Start shopping to see your orders here.</p>
                  <Link to="/store">
                    <Button className="bg-indigo-600 hover:bg-indigo-700 rounded-full">
                      Browse Store
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {orders.map((order, idx) => (
                    <div key={order.order_id} className="p-4 border border-slate-200 rounded-xl" data-testid={`order-${idx}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-mono text-sm text-slate-500">{order.order_id}</span>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          order.status === "paid" ? "bg-emerald-100 text-emerald-700" :
                          order.status === "pending" ? "bg-amber-100 text-amber-700" :
                          "bg-slate-100 text-slate-700"
                        }`}>
                          {order.status}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-slate-600">
                          <CreditCard className="h-4 w-4" />
                          <span>£{order.total_amount?.toFixed(2)}</span>
                        </div>
                        <span className="text-sm text-slate-500">
                          Qty: {order.quantity}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : activeTab === "subscriptions" ? (
              subscriptions.length === 0 ? (
                <div className="text-center py-12">
                  <RefreshCw className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-heading font-semibold text-slate-900 mb-2">No subscriptions</h3>
                  <p className="text-slate-500 mb-4">Subscribe to products to see them here.</p>
                  <Link to="/store">
                    <Button className="bg-indigo-600 hover:bg-indigo-700 rounded-full">
                      Browse Store
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {subscriptions.map((sub, idx) => (
                    <div key={sub.subscription_id} className="p-4 border border-slate-200 rounded-xl" data-testid={`subscription-${idx}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-mono text-sm text-slate-500">{sub.subscription_id}</span>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          sub.status === "active" ? "bg-emerald-100 text-emerald-700" :
                          "bg-red-100 text-red-700"
                        }`}>
                          {sub.status}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-slate-600">
                          <Calendar className="h-4 w-4" />
                          <span>Next billing: {new Date(sub.next_billing_date).toLocaleDateString()}</span>
                        </div>
                        {sub.status === "active" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCancelSubscription(sub.subscription_id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`cancel-sub-${idx}`}
                          >
                            <X className="h-4 w-4 mr-1" />
                            Cancel
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : (
              signatures.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-heading font-semibold text-slate-900 mb-2">No signed agreements</h3>
                  <p className="text-slate-500">Your signed agreements will appear here.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {signatures.map((sig, idx) => (
                    <div key={sig.signature_id} className="p-4 border border-slate-200 rounded-xl" data-testid={`agreement-${idx}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-sm text-slate-500">{sig.signature_id}</span>
                        <span className="text-sm text-slate-500">
                          Signed: {new Date(sig.signed_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default CustomerDashboard;
